import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function DigiLockerCallback() {
  const [searchParams] = useSearchParams();
  const [hasNotified, setHasNotified] = useState(false);
  
  // Derive status from URL params
  const { status, message, sessionId } = useMemo(() => {
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");
    const sessionId = searchParams.get("session_id");
    
    if (error) {
      return {
        status: "error",
        message: errorDescription || error || "DigiLocker authorization failed",
        sessionId
      };
    }
    
    return {
      status: "success",
      message: "DigiLocker authorization successful! Closing window...",
      sessionId
    };
  }, [searchParams]);

  useEffect(() => {
    if (hasNotified) return;
    
    // Notify parent window
    if (window.opener) {
      window.opener.postMessage({ 
        type: "DIGILOCKER_CALLBACK", 
        status,
        error: status === "error" ? message : undefined,
        sessionId 
      }, "*");
    }
    
    setHasNotified(true);

    // Auto close popup after delay
    const closeDelay = status === "error" ? 3000 : 2000;
    const timer = setTimeout(() => {
      // Try multiple methods to close
      try {
        window.close();
      } catch (e) {
        console.log("window.close() failed:", e);
      }
      
      // If window.close() didn't work, try alternative
      setTimeout(() => {
        if (!window.closed) {
          // Show message that user needs to close manually
          document.body.innerHTML = `
            <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;text-align:center;padding:20px;">
              <div>
                <p style="font-size:18px;margin-bottom:10px;">✅ Authorization Complete</p>
                <p style="color:#666;">Please close this window manually and return to the main page.</p>
              </div>
            </div>
          `;
        }
      }, 500);
    }, closeDelay);
    
    return () => clearTimeout(timer);
  }, [status, message, sessionId, hasNotified]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center space-y-6 max-w-md">
        {status === "success" && (
          <>
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #F2B721 0%, #F9D96A 50%, #F2B721 100%)' }}
            >
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
            <h1 className="text-xl font-bold text-green-600" style={{ fontFamily: 'Manrope' }}>
              Authorization Successful!
            </h1>
          </>
        )}
        
        {status === "error" && (
          <>
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center bg-red-100">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-red-600" style={{ fontFamily: 'Manrope' }}>
              Authorization Failed
            </h1>
          </>
        )}
        
        <p className="text-muted-foreground">{message}</p>
        
        <p className="text-xs text-muted-foreground">
          This window will close automatically...
        </p>
        
        <button 
          onClick={() => window.close()}
          className="text-sm text-primary underline hover:no-underline"
        >
          Close manually
        </button>
      </div>
    </div>
  );
}
